<template>
    <div v-if="visible" class="ai-assist">
        <transition name="ai-assist-pop">
            <div v-if="panelOpen" class="ai-assist-panel" role="dialog" :aria-label="$t('ai_offer.assistant_title')">
                <div class="ai-assist-top">
                    <div class="ai-assist-brand">
                        <span class="ai-assist-logo">
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="#fff" aria-hidden="true">
                                <path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path>
                                <path d="M14.75 12.25L15.4 14.1L17.25 14.75L15.4 15.4L14.75 17.25L14.1 15.4L12.25 14.75L14.1 14.1L14.75 12.25Z"></path>
                            </svg>
                        </span>
                        <span>{{ $t("ai_offer.assistant_title") }}</span>
                    </div>
                    <button type="button" class="ai-assist-x" :aria-label="$t('ai_offer.assistant_close')" @click="panelOpen = false">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true">
                            <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                    </button>
                    <div class="ai-assist-hello">{{ $t("ai_offer.assistant_hello") }}</div>
                    <div class="ai-assist-sub">{{ $t("ai_offer.assistant_sub") }}</div>
                </div>

                <div class="ai-assist-body">
                    <button type="button" class="ai-assist-action main" @click="startOffer">
                        <span class="ic">
                            <svg width="20" height="20" viewBox="0 0 18 18" fill="#212541" aria-hidden="true">
                                <path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path>
                            </svg>
                        </span>
                        <span class="txt">
                            <b>{{ $t("ai_offer.button_generate") }}</b>
                            <small>{{ $t("ai_offer.assistant_action_sub") }}</small>
                        </span>
                        <paid-modal-icon v-if="!allowed" :openModal="paidOpen" @close="paidOpen = false" />
                        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#212541" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </button>

                    <button v-if="statusRow" type="button" class="ai-assist-action draft" @click="onStatusClick">
                        <span class="txt">
                            <b>{{ statusRow.title }}</b>
                            <small>{{ statusRow.sub }}</small>
                        </span>
                        <span class="go">{{ statusRow.cta }}</span>
                    </button>

                    <div class="ai-assist-soon">
                        <span>{{ $t("ai_offer.assistant_soon_title") }}</span>
                        <em>{{ $t("ai_offer.assistant_soon") }}</em>
                    </div>
                    <p class="ai-assist-soon-text">{{ $t("ai_offer.assistant_soon_text") }}</p>
                </div>
            </div>
        </transition>

        <transition name="ai-assist-pop">
            <button v-if="bubble" type="button" class="ai-assist-bubble" @click="openResult">
                <span class="ok">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#212541" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                </span>
                <span>
                    <b>{{ $t("ai_offer.assistant_done") }}</b>
                    <small>{{ $t("ai_offer.assistant_open") }}</small>
                </span>
            </button>
        </transition>

        <button
            type="button"
            class="ai-assist-launch"
            :class="{ busy: status.phase === 'generating', open: panelOpen }"
            :aria-label="$t('ai_offer.assistant_title')"
            @click="toggle"
        >
            <span v-if="status.phase === 'generating'" class="spin"></span>
            <svg width="26" height="26" viewBox="0 0 18 18" fill="#2ed8b6" aria-hidden="true">
                <path d="M9 1.5L10.6 6.4L15.5 8L10.6 9.6L9 14.5L7.4 9.6L2.5 8L7.4 6.4L9 1.5Z"></path>
                <path d="M14.75 12.25L15.4 14.1L17.25 14.75L15.4 15.4L14.75 17.25L14.1 15.4L12.25 14.75L14.1 14.1L14.75 12.25Z"></path>
            </svg>
            <span v-if="status.phase === 'done'" class="badge">1</span>
        </button>
    </div>
</template>

<script>
import { EventBus } from "./EventBus.js";
import PaidModalIcon from "../../vue_components/PaidModalIcon.vue";

// Stranky, kde by spustac zakryl spodnu listu editora ponuky.
const SKRYT_NA = [/^\/admin\/offers\/create/, /^\/admin\/offers\/[^/]+\/edit/];

export default {
    name: "AiAssistant",
    components: { PaidModalIcon },
    props: {
        is_paid: { type: [Number, String, Boolean], default: 0 },
    },
    data() {
        return {
            panelOpen: false,
            paidOpen: false,
            modalOpen: false,
            bubble: false,
            status: { phase: "idle", title: "", editUrl: "" },
            onPage: !SKRYT_NA.some((re) => re.test(window.location.pathname)),
        };
    },
    computed: {
        allowed() {
            return Number(this.is_paid) === 1 || this.is_paid === true;
        },
        visible() {
            return this.onPage && !this.modalOpen;
        },
        statusRow() {
            const s = this.status;
            if (s.phase === "generating") {
                return { title: this.$t("ai_offer.assistant_generating"), sub: s.title, cta: this.$t("ai_offer.assistant_show") };
            }
            if (s.phase === "done") {
                return { title: this.$t("ai_offer.assistant_done"), sub: s.title, cta: this.$t("ai_offer.assistant_open") };
            }
            if (s.phase === "draft") {
                return { title: this.$t("ai_offer.assistant_draft"), sub: s.title, cta: this.$t("ai_offer.assistant_continue") };
            }
            return null;
        },
    },
    mounted() {
        EventBus.$on("ai-offer-status", this.onStatus);
    },
    beforeDestroy() {
        EventBus.$off("ai-offer-status", this.onStatus);
    },
    methods: {
        onStatus(s) {
            this.modalOpen = !!s.open;
            this.status = { phase: s.phase || "idle", title: s.title || "", editUrl: s.editUrl || "" };
            this.bubble = !s.open && s.phase === "done";
            if (s.open) this.panelOpen = false;
        },
        toggle() {
            if (this.bubble) {
                this.openResult();
                return;
            }
            this.panelOpen = !this.panelOpen;
        },
        startOffer() {
            if (!this.allowed) {
                this.paidOpen = true;
                return;
            }
            this.panelOpen = false;
            EventBus.$emit("open-ai-offer-modal");
        },
        onStatusClick() {
            if (this.status.phase === "done") {
                this.openResult();
                return;
            }
            this.panelOpen = false;
            EventBus.$emit("open-ai-offer-modal", "resume");
        },
        openResult() {
            if (this.status.editUrl) window.location.href = this.status.editUrl;
        },
    },
};
</script>

<style lang="scss" scoped>
.ai-assist {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1150;
    font-family: Roboto, sans-serif;
}
.ai-assist-launch {
    position: relative;
    width: 60px;
    height: 60px;
    border-radius: 19px;
    border: 0;
    background: #212541;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 14px 34px rgba(33, 37, 65, 0.38);
    transition: transform 0.2s;
    &:hover {
        transform: translateY(-2px) scale(1.04);
    }
    svg {
        position: relative;
        transition: transform 0.3s;
    }
    &.open svg {
        transform: rotate(90deg);
    }
    .spin {
        position: absolute;
        inset: 6px;
        border-radius: 50%;
        border: 3px solid rgba(255, 255, 255, 0.15);
        border-top-color: #2ed8b6;
        animation: ai-assist-spin 0.9s linear infinite;
    }
    .badge {
        position: absolute;
        top: -6px;
        right: -6px;
        min-width: 22px;
        height: 22px;
        border-radius: 11px;
        border: 3px solid #f4f6f9;
        background: #2ed8b6;
        color: #212541;
        font: 700 11px Roboto, sans-serif;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
.ai-assist-panel {
    position: absolute;
    right: 0;
    bottom: 76px;
    width: 380px;
    max-width: calc(100vw - 32px);
    background: #fff;
    border-radius: 22px;
    box-shadow: 0 30px 70px rgba(20, 22, 45, 0.32);
    overflow: hidden;
    transform-origin: bottom right;
}
.ai-assist-top {
    position: relative;
    background: #212541;
    color: #fff;
    padding: 18px 20px 30px;
}
.ai-assist-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    font: 700 15px Montserrat, sans-serif;
}
.ai-assist-logo {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #f3385a;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ai-assist-x {
    position: absolute;
    top: 18px;
    right: 18px;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    border: 0;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ai-assist-hello {
    font: 800 21px/1.3 Montserrat, sans-serif;
    margin-top: 16px;
}
.ai-assist-sub {
    font: 400 14px Roboto, sans-serif;
    color: rgba(255, 255, 255, 0.78);
    margin-top: 4px;
}
.ai-assist-body {
    position: relative;
    margin-top: -12px;
    background: #fff;
    border-radius: 16px 16px 0 0;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.ai-assist-action {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    border: 0;
    border-radius: 16px;
    padding: 14px 16px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: transform 0.18s;
    &:hover {
        transform: translateY(-2px);
    }
    .txt {
        flex: 1;
        b {
            display: block;
            font: 700 14.5px Montserrat, sans-serif;
            color: #212541;
        }
        small {
            display: block;
            font: 400 12.5px Roboto, sans-serif;
            color: #4a5164;
            margin-top: 3px;
        }
    }
    &.main {
        background: #eafbf6;
        box-shadow: inset 0 0 0 1.5px #bff0e3;
        .ic {
            width: 42px;
            height: 42px;
            border-radius: 12px;
            background: #2ed8b6;
            display: flex;
            align-items: center;
            justify-content: center;
            flex: none;
        }
    }
    &.draft {
        background: #f8fafb;
        box-shadow: inset 0 0 0 1.5px #e3e8ee;
        .go {
            font: 700 12.5px Roboto, sans-serif;
            color: #c81f45;
        }
    }
}
.ai-assist-soon {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
    span {
        font: 700 11.5px Roboto, sans-serif;
        color: #4a5164;
        letter-spacing: 0.06em;
        text-transform: uppercase;
    }
    em {
        font: 600 11px Roboto, sans-serif;
        font-style: normal;
        color: #4a5164;
        background: #eef1f5;
        border-radius: 999px;
        padding: 3px 9px;
    }
}
.ai-assist-soon-text {
    margin: 0;
    font: 400 13px/1.5 Roboto, sans-serif;
    color: #5b6275;
}
.ai-assist-bubble {
    position: absolute;
    right: 76px;
    bottom: 6px;
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
    background: #fff;
    border: 0;
    border-radius: 14px;
    padding: 12px 16px;
    box-shadow: 0 16px 40px rgba(33, 37, 65, 0.25);
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    .ok {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: #2ed8b6;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    b {
        display: block;
        font: 700 13.5px Montserrat, sans-serif;
        color: #212541;
    }
    small {
        display: block;
        font: 600 12.5px Roboto, sans-serif;
        color: #c81f45;
        margin-top: 2px;
    }
}
.ai-assist-pop-enter-active,
.ai-assist-pop-leave-active {
    transition: opacity 0.25s, transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.ai-assist-pop-enter,
.ai-assist-pop-leave-to {
    opacity: 0;
    transform: translateY(14px) scale(0.94);
}
@keyframes ai-assist-spin {
    to {
        transform: rotate(360deg);
    }
}
@media (max-width: 767px) {
    .ai-assist {
        right: 16px;
        bottom: 16px;
    }
    .ai-assist-bubble {
        right: 0;
        bottom: 72px;
    }
}
</style>
